import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';

import { useGetAcademicTerms } from '../../hooks/academicTerms/useGetAcademicTerms';
import { useDeleteAcademicTerm } from '../../hooks/academicTerms/useDeleteAcademicTerm';
import { useDeleteAllAcademicTerms } from '../../hooks/academicTerms/useDeleteAllAcademicTerms';
import { useForceDeleteAcademicTerm } from '../../hooks/academicTerms/useForceDeleteAcademicTerm';
import { useForceDeleteAllAcademicTerms } from '../../hooks/academicTerms/useForceDeleteAllAcademicTerms';
import { useRestoreAcademicTerm } from '../../hooks/academicTerms/useRestoreAcademicTerm';
import { useRestoreAllAcademicTerms } from '../../hooks/academicTerms/useRestoreAllAcademicTerms';
import { useGetAcademicYears } from '../../hooks/academicYears/useGetAcademicYears';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import AcademicTermsTableColumns from './Table/AcademicTermsTableColumns';
import AcademicTermsTableRow from './Table/AcademicTermsTableRow';

const AcademicTermsTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const [filterYearId, setFilterYearId] = useState('');

    const perms = useMemo(() => ({
        list: has('academic_terms_list'),
        create: has('academic_terms_create'),
        edit: has('academic_terms_edit'),
        delete: has('academic_terms_delete'),
        archive_list: has('academic_terms_archive_list'),
        restore: has('academic_terms_restore'),
        force_delete: has('academic_terms_force_delete'),
    }), [has]);

    const { deleteAcademicTerm, isDelete } = useDeleteAcademicTerm();
    const { deleteAllAcademicTerms, isDelete: isDeletingAll } = useDeleteAllAcademicTerms();
    const { forceDeleteAcademicTerm } = useForceDeleteAcademicTerm();
    const { forceDeleteAllAcademicTerms } = useForceDeleteAllAcademicTerms();
    const { restoreAcademicTerm } = useRestoreAcademicTerm();
    const { restoreAllAcademicTerms } = useRestoreAllAcademicTerms();

    const { academicYears } = useGetAcademicYears(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/academic-terms',
        deleteSingle: deleteAcademicTerm,
        deleteAll: deleteAllAcademicTerms,
        forceDeleteSingle: forceDeleteAcademicTerm,
        forceDeleteAll: forceDeleteAllAcademicTerms,
        restoreSingle: restoreAcademicTerm,
        restoreAll: restoreAllAcademicTerms,
        isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        academic_year_id: filterYearId || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterYearId, isArchive]);

    const { isLoading, error, academicTerms, meta } = useGetAcademicTerms(page, perPage, filters);

    const renderRow = (params) => (
        <AcademicTermsTableRow
            key={params.key || params.item.id}
            item={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={params.isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={academicTerms.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    value={filterYearId}
                    onChange={(e) => setFilterYearId(e.target.value)}
                    label={isArabic ? 'فلتر حسب السنة' : 'Filter by Year'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{
                        minWidth: 220,
                        ...(isArabic && {
                            '& .MuiInputLabel-root': {
                                left: 'unset',
                                right: 32,
                                transformOrigin: 'top right',
                            },
                            '& .MuiInputLabel-shrink': {
                                right: 32,
                            },
                            '& .MuiSelect-icon': { left: 7, right: 'unset' },
                            '& .MuiSelect-select': {
                                textAlign: 'right',
                                paddingRight: '14px !important',
                                paddingLeft: '32px !important',
                            },
                            '& .MuiOutlinedInput-notchedOutline legend': {
                                textAlign: 'right',
                            },
                        }),
                    }}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: {
                                sx: { direction: isArabic ? 'rtl' : 'ltr' },
                            },
                        },
                    }}
                >
                    <MenuItem value="">
                        {isArabic ? 'الكل' : 'All'}
                    </MenuItem>
                    {(academicYears || []).map((y) => (
                        <MenuItem key={y.id} value={y.id}>
                            {y.name}
                        </MenuItem>
                    ))}
                </TextField>

                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && (
                        <ArchiveButton to="/academic-terms/archive" />
                    )}
                    {isArchive && perms.list && (
                        <ArchiveButton
                            to="/academic-terms"
                            archiveLabelAr="العودة للقائمة"
                            archiveLabelEn="Back to List"
                        />
                    )}
                </Box>
            </Box>

            <TableLayout
                title={isArabic
                    ? (isArchive ? 'أرشيف الفصول الدراسية' : 'الفصول الدراسية')
                    : (isArchive ? 'Academic Terms Archive' : 'Academic Terms')}
                subtitle={isArabic
                    ? (isArchive ? 'إدارة الفصول المحذوفة' : 'إدارة الفصول الدراسية في النظام')
                    : (isArchive ? 'Manage deleted terms' : 'Manage academic terms')}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة فصل دراسي' : 'Add Term'}
                onAddClick={() => navigate('/academic-terms/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading}
                error={error}
                items={academicTerms}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, academicTerms)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={AcademicTermsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive
                    ? (perms.force_delete ? handleMenuDelete : undefined)
                    : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد فصول في الأرشيف' : 'لا توجد فصول دراسية'}
                emptyLabelEn={isArchive ? 'No archived terms' : 'No academic terms'}
                loadErrorAr="حدث خطأ أثناء تحميل الفصول الدراسية"
                loadErrorEn="Failed to load academic terms"
            />
        </>
    );
};

export default AcademicTermsTable;
